import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, PaletteMode, ThemeProvider } from '@mui/material';
import { createAppTheme } from '../theme';

/** Shared with mobile app — keep values in sync (see `mobile/src/context/ThemeModeContext.tsx`). */
export const THEME_MODE_STORAGE_KEY = 'ci-theme-mode';

type ThemeModeContextValue = {
  mode: PaletteMode;
  isDarkMode: boolean;
  setThemeMode: (mode: PaletteMode) => void;
  toggleThemeMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  isDarkMode: false,
  setThemeMode: () => {},
  toggleThemeMode: () => {},
});

const getInitialThemeMode = (): PaletteMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return storedMode === 'dark' ? 'dark' : 'light';
};

interface ThemeModeProviderProps {
  children: React.ReactNode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialThemeMode);

  useEffect(() => {
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  }, [mode]);

  const setThemeMode = useCallback((nextMode: PaletteMode) => {
    setMode(nextMode);
  }, []);

  const toggleThemeMode = useCallback(() => {
    setMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light'));
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDarkMode: mode === 'dark',
      setThemeMode,
      toggleThemeMode,
    }),
    [mode, setThemeMode, toggleThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
